import { useState, useEffect, useRef } from 'react';
import { Calendar as CalendarIcon, MapPin, Clock, Users, ExternalLink, ChevronDown, ChevronUp, Loader2, Image as ImageIcon, UserPlus, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('de-CH', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

const formatTime = (timeStr) => timeStr ? timeStr.substring(0, 5) + ' Uhr' : '';

export default function PublicEventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const containerRef = useRef(null);

  const sendHeight = () => {
    if (containerRef.current) {
      window.parent.postMessage({ type: 'shrt-resize', height: containerRef.current.scrollHeight + 20 }, '*');
    }
  };

  useEffect(() => { 
    fetch(`${API_URL}/api/events/public/list?upcoming_only=true&limit=50`)
      .then(r => r.json())
      .then(d => setEvents(d.events || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { sendHeight(); setTimeout(sendHeight, 100); setTimeout(sendHeight, 500); setTimeout(sendHeight, 1500); }, [events, expandedId, loading]);
  useEffect(() => { window.addEventListener('resize', sendHeight); return () => window.removeEventListener('resize', sendHeight); }, []);

  if (loading) return (
    <div ref={containerRef} style={{ display: 'flex', justifyContent: 'center', padding: '48px', background: 'transparent' }}>
      <Loader2 className="animate-spin" style={{ color: '#d5001c', width: 32, height: 32 }} />
    </div>
  );
  
  if (!events.length) return (
    <div ref={containerRef} style={{ textAlign: 'center', padding: '48px', color: '#333', background: 'transparent' }}>
      <CalendarIcon style={{ width: 48, height: 48, margin: '0 auto 16px', color: '#999' }} />
      <p style={{ fontSize: 18 }}>Zurzeit keine Events</p>
      <div style={{ marginTop: 32, paddingTop: 24 }}>
        <a 
          href="/login"
          target="_blank"
          rel="noopener noreferrer"
          style={{ 
            display: 'inline-block',
            background: '#d5001c', 
            color: '#fff', 
            padding: '14px 28px', 
            fontSize: 14, 
            fontWeight: 700, 
            textTransform: 'uppercase', 
            textDecoration: 'none'
          }}
        >
          Zum Mitgliederbereich →
        </a>
      </div>
    </div>
  );

  // Separate upcoming and past events
  const upcomingEvents = events.filter(e => !e.is_past);
  const pastEvents = events.filter(e => e.is_past);

  return (
    <div ref={containerRef} style={{ background: '#ffffff', fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
      {/* Styles for HTML content and mobile responsiveness */}
      <style>{`
        /* Desktop: proportional grössere Schrift */
        @media (min-width: 768px) {
          .pe-section-title { font-size: 26px !important; }
          .pe-event-date { font-size: 30px !important; }
          .pe-event-month { font-size: 12px !important; }
          .pe-event-title { font-size: 18px !important; }
          .pe-event-meta { font-size: 14px !important; }
          .pe-event-meta svg { width: 14px !important; height: 14px !important; }
          .pe-badge { font-size: 10px !important; padding: 2px 7px !important; }
          .pe-expanded-meta { font-size: 15px !important; }
          .pe-expanded-meta svg { width: 15px !important; height: 15px !important; }
          .pe-lead { font-size: 16px !important; line-height: 1.7 !important; }
          .pe-btn { font-size: 14px !important; padding: 10px 18px !important; }
          .pe-cta-btn { font-size: 13px !important; padding: 11px 20px !important; white-space: nowrap !important; }
          .pe-spots { font-size: 15px !important; }
          .pe-price { font-size: 16px !important; }
          .pe-gallery-title { font-size: 15px !important; }
          .event-html-content { font-size: 16px !important; line-height: 1.7 !important; }
          .event-html-content h1 { font-size: 28px !important; }
          .event-html-content h2 { font-size: 22px !important; }
          .event-html-content h3 { font-size: 18px !important; }
          .event-html-content h4 { font-size: 16px !important; }
          .event-image-container { width: 150px !important; min-width: 150px !important; height: 110px !important; }
        }
        /* Mobile responsiveness */
        @media (max-width: 500px) {
          .event-image-container {
            display: none !important;
          }
          .event-action-button {
            flex-basis: 100% !important;
            width: 100% !important;
            min-width: unset !important;
          }
        }
        @media (min-width: 501px) {
          .event-action-button {
            flex-basis: auto !important;
            width: auto !important;
            min-width: auto !important;
            flex-grow: 0 !important;
          }
        }
        
        .event-html-content h1 {
          font-size: 28px;
          font-weight: 700;
          color: #111;
          margin: 0 0 16px 0;
          line-height: 1.3;
        }
        .event-html-content h2 {
          font-size: 22px;
          font-weight: 700;
          color: #111;
          margin: 24px 0 12px 0;
          line-height: 1.3;
        }
        .event-html-content h3 {
          font-size: 18px;
          font-weight: 700;
          color: #222;
          margin: 20px 0 10px 0;
          line-height: 1.3;
        }
        .event-html-content h4 {
          font-size: 16px;
          font-weight: 700;
          color: #333;
          margin: 16px 0 8px 0;
        }
        .event-html-content p {
          margin: 0 0 12px 0;
        }
        .event-html-content ul, .event-html-content ol {
          margin: 12px 0;
          padding-left: 24px;
        }
        .event-html-content ul {
          list-style-type: disc;
        }
        .event-html-content ol {
          list-style-type: decimal;
        }
        .event-html-content li {
          margin: 6px 0;
          padding-left: 4px;
        }
        .event-html-content li p {
          margin: 0;
          padding: 0;
        }
        .event-html-content strong, .event-html-content b {
          font-weight: 700;
        }
        .event-html-content em, .event-html-content i {
          font-style: italic;
        }
        .event-html-content a {
          color: #d5001c;
          text-decoration: underline;
        }
        .event-html-content a:hover {
          text-decoration: none;
        }
        .event-html-content blockquote {
          border-left: 3px solid #d5001c;
          padding-left: 16px;
          margin: 16px 0;
          font-style: italic;
          color: #555;
        }
      `}</style>
      
      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <>
          <h2 className="pe-section-title" style={{ fontSize: 20, fontWeight: 700, color: '#111', margin: '0 0 20px', textTransform: 'uppercase', letterSpacing: 1.5 }}>
            Kommende Events
          </h2>
          {upcomingEvents.map((event) => (
            <EventCard 
              key={event.id} 
              event={event} 
              isOpen={expandedId === event.id}
              onToggle={() => setExpandedId(expandedId === event.id ? null : event.id)}
              onImageClick={setLightboxImage}
            />
          ))}
        </>
      )}
      
      {/* Past Events with Reviews */}
      {pastEvents.length > 0 && (
        <>
          <h2 className="pe-section-title" style={{ fontSize: 20, fontWeight: 700, color: '#111', margin: '32px 0 20px', textTransform: 'uppercase', letterSpacing: 1.5, borderTop: '2px solid #ddd', paddingTop: 32 }}>
            Rückblicke
          </h2>
          {pastEvents.map((event) => (
            <EventCard 
              key={event.id} 
              event={event} 
              isOpen={expandedId === event.id}
              onToggle={() => setExpandedId(expandedId === event.id ? null : event.id)}
              onImageClick={setLightboxImage}
              isPastEvent
            />
          ))}
        </>
      )}
      
      {/* Member Area Button - Bottom */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: 32, 
        paddingTop: 24, 
        borderTop: '1px solid #ddd'
      }}>
        <a 
          href="/login"
          target="_blank"
          rel="noopener noreferrer"
          style={{ 
            display: 'inline-block',
            background: '#1a1a1a', 
            color: '#fff', 
            padding: '14px 28px', 
            fontSize: 14, 
            fontWeight: 700, 
            textTransform: 'uppercase', 
            textDecoration: 'none'
          }}
        >
          Zum Mitgliederbereich →
        </a>
      </div>
      
      {/* Lightbox for gallery images */}
      {lightboxImage && (
        <div 
          onClick={() => setLightboxImage(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            cursor: 'pointer'
          }}
        >
          <img 
            src={lightboxImage} 
            alt="Vergrössert"
            style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }}
          />
        </div>
      )}
    </div>
  );
}

// Event Card Component
function EventCard({ event, isOpen, onToggle, onImageClick, isPastEvent = false }) {
  const d = new Date(event.date);
  const spots = event.max_participants ? event.max_participants - (event.stats?.total_participants || 0) : null;
  const full = spots !== null && spots <= 0;

  return (
    <div style={{ marginBottom: 16 }}>
      {/* Event Card */}
      <div 
        style={{ 
          background: isOpen ? '#fff' : '#f8f8f8',
          border: `1px solid ${isOpen ? '#d5001c' : '#ddd'}`,
          borderLeftWidth: isOpen ? 4 : 1,
          display: 'flex',
          flexWrap: 'wrap',
          overflow: 'hidden'
        }}
      >
        {/* Image Left - Hidden on mobile when not expanded */}
        {event.image_url && (
          <div style={{ 
            width: 120, 
            minWidth: 120,
            height: 90,
            flexShrink: 0,
            overflow: 'hidden',
            background: '#eee',
            display: window.innerWidth < 500 && !isOpen ? 'none' : 'block'
          }}
          className="event-image-container"
          >
            <img 
              src={event.image_url} 
              alt={event.title}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        )}
        
        {/* Content Middle */}
        <div
          onClick={onToggle}
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
          style={{ flex: 1, padding: '12px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, overflow: 'hidden' }}
        >
          {/* Date */}
          <div style={{ textAlign: 'center', minWidth: 48, flexShrink: 0 }}>
            <div className="pe-event-date" style={{ color: isPastEvent ? '#888' : '#d5001c', fontSize: 24, fontWeight: 700, lineHeight: 1 }}>{d.getDate()}</div>
            <div className="pe-event-month" style={{ color: '#888', fontSize: 10, textTransform: 'uppercase', marginTop: 3, fontWeight: 600, letterSpacing: 1 }}>
              {d.toLocaleDateString('de-CH', { month: 'short' })}
            </div>
          </div>
          
          {/* Info */}
          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <h3 className="pe-event-title" style={{ color: '#111', fontSize: 15, fontWeight: 700, margin: 0, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{event.title}</h3>
              {isPastEvent && event.has_review && (
                <span className="pe-badge" style={{ background: '#d5001c', color: '#fff', fontSize: 9, padding: '2px 6px', fontWeight: 600, flexShrink: 0 }}>RÜCKBLICK</span>
              )}
            </div>
            <div className="pe-event-meta" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 12, color: '#555' }}>
              {event.time && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Clock size={12} />{formatTime(event.time)}</span>}
              {event.location && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><MapPin size={12} />{event.location}</span>}
            </div>
          </div>
          
          {/* Expand Icon */}
          {isOpen ? (
            <ChevronUp size={16} style={{ color: '#d5001c', flexShrink: 0 }} />
          ) : (
            <ChevronDown size={16} style={{ color: '#999', flexShrink: 0 }} />
          )}
        </div>
        
        {/* Button Right - Shows below on mobile */}
        {!isPastEvent && !isOpen && (event.registration_open || event.link) && (
          <a 
            href={event.registration_open 
              ? `/login?returnUrl=${encodeURIComponent('/events/' + event.id)}`
              : event.link
            }
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: event.registration_open ? '#d5001c' : '#1a1a1a', 
              color: '#fff', 
              padding: '14px 24px',
              fontSize: 12, 
              fontWeight: 700, 
              textTransform: 'uppercase', 
              textDecoration: 'none',
              letterSpacing: 1,
              whiteSpace: 'nowrap',
              textAlign: 'center',
              flexShrink: 0,
              boxSizing: 'border-box'
            }}
            className="event-action-button pe-btn"
          >
            {event.registration_open ? 'Zur Anmeldung →' : (event.link_text || 'Mehr dazu') + ' →'}
          </a>
        )}
      </div>

      {/* Expanded Detail */}
      {isOpen && (
        <div style={{ 
          background: '#fff', 
          border: '1px solid #d5001c', 
          borderTop: 'none', 
          padding: 20, 
          marginTop: -1
        }}>
          
          {/* Event Content or Review Text */}
          {isPastEvent && event.review_text ? (
            <div 
              style={{ color: '#333', fontSize: 15, lineHeight: 1.8, marginBottom: 20 }} 
              dangerouslySetInnerHTML={{ __html: event.review_text }}
              className="event-html-content"
            />
          ) : event.body_html ? (
            <div 
              style={{ color: '#333', fontSize: 15, lineHeight: 1.8, marginBottom: 20 }} 
              dangerouslySetInnerHTML={{ __html: event.body_html }}
              className="event-html-content"
            />
          ) : event.lead && (
            <p className="pe-lead" style={{ color: '#333', fontSize: 15, lineHeight: 1.8, marginBottom: 20 }}>{event.lead}</p>
          )}
          
          {/* Review Image Preview - Only one image, rest requires login */}
          {isPastEvent && event.review_images?.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                {/* Single preview image */}
                <div 
                  onClick={() => onImageClick(typeof event.review_images[0] === 'string' ? event.review_images[0] : event.review_images[0]?.url)}
                  style={{ 
                    width: 180,
                    minWidth: 180,
                    aspectRatio: '4/3', 
                    overflow: 'hidden', 
                    background: '#eee',
                    cursor: 'pointer'
                  }}
                >
                  <img 
                    src={typeof event.review_images[0] === 'string' ? event.review_images[0] : (event.review_images[0]?.thumb || event.review_images[0]?.url)} 
                    alt="Rückblick Vorschau"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
                {/* Login hint for more images */}
                {event.review_images.length > 1 && (
              <div className="pe-gallery-title" style={{ flex: 1, padding: '8px 0' }}>
                    <p style={{ fontSize: 14, color: '#555', margin: '0 0 8px' }}>
                      <strong>{event.review_images.length} Bilder</strong> in der Galerie
                    </p>
                    <a 
                      href={`/login?returnUrl=${encodeURIComponent('/events/' + event.id)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 13,
                        color: '#d5001c',
                        fontWeight: 600,
                        textDecoration: 'none'
                      }}
                    >
                      <ImageIcon size={14} />
                      Alle Bilder im Mitgliederbereich →
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Review PDFs */}
          {isPastEvent && event.review_pdfs?.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              {event.review_pdfs.map((pdf, idx) => (
                <a
                  key={idx}
                  href={pdf.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 13,
                    color: '#d5001c',
                    fontWeight: 600,
                    textDecoration: 'none',
                    marginRight: 16,
                    marginBottom: 6
                  }}
                >
                  <FileText size={14} />
                  {pdf.filename || `PDF ${idx + 1}`}
                </a>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, paddingTop: 14, borderTop: '1px solid #eee' }}>
            {/* External Link - only show if booking is disabled OR always show if it exists and booking is enabled */}
            {event.link && (
              <a href={event.link} target="_blank" rel="noopener noreferrer" className="pe-cta-btn" style={{ 
                background: event.registration_open ? '#1a1a1a' : '#d5001c', 
                color: '#fff', 
                padding: '10px 20px', 
                fontSize: 12, 
                fontWeight: 700, 
                textTransform: 'uppercase', 
                textDecoration: 'none', 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: 6,
                whiteSpace: 'nowrap'
              }}>
                {event.link_text || 'Mehr erfahren'}<ExternalLink size={12} />
              </a>
            )}
            {/* Internal booking - only show if registration is open */}
            {!isPastEvent && event.registration_open && (
              <a href={`/login?returnUrl=${encodeURIComponent('/events/' + event.id)}`} target="_blank" rel="noopener noreferrer" className="pe-cta-btn" style={{ 
                background: '#d5001c', 
                color: '#fff', 
                padding: '10px 20px', 
                fontSize: 12, 
                fontWeight: 700, 
                textTransform: 'uppercase', 
                textDecoration: 'none', 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: 6,
                whiteSpace: 'nowrap'
              }}>
                Mitglieder-Anmeldung →
              </a>
            )}
            {/* Guest registration - link to public event detail page in new window */}
            {!isPastEvent && event.registration_open && event.guest_registration_enabled && (
              <a 
                href={`https://member.shrt.ch/public/event/${event.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="pe-cta-btn event-action-button" 
                style={{ 
                  background: '#1a1a1a', 
                  color: '#fff', 
                  padding: '10px 20px', 
                  fontSize: 12, 
                  fontWeight: 700, 
                  textTransform: 'uppercase', 
                  border: 'none',
                  cursor: 'pointer',
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  gap: 6,
                  whiteSpace: 'nowrap',
                  textDecoration: 'none'
                }}
                data-testid={`guest-register-btn-${event.id}`}
              >
                <UserPlus size={14} />
                Als Gast anmelden
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


